import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  CreditCard, 
  FileText, 
  Download, 
  Crown, 
  Sparkles,
  Clock,
  Gift,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Tag,
  Receipt
} from "lucide-react";
import { toast } from "sonner";
import api, { paymentsAPI } from "../lib/api";
import AnimatedLogo from "../components/AnimatedLogo";

export default function BillingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [freeOperations, setFreeOperations] = useState(0);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("annual");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subRes, invoicesRes, pricingRes] = await Promise.all([
        api.get("/subscription/current"),
        api.get("/invoices"),
        api.get("/pricing")
      ]);
      
      setSubscription(subRes.data.subscription);
      setFreeOperations(subRes.data.free_operations || 0);
      setIsTrialActive(subRes.data.is_trial_active);
      setTrialEndDate(subRes.data.trial_end_date);
      setInvoices(invoicesRes.data);
      setPricing(pricingRes.data.pricing);
    } catch (error) {
      console.error("Error loading billing data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setPromoValidating(true);
    try {
      const res = await api.post(`/promo-codes/validate?code=${promoCode}&plan_type=${selectedPlan}`);
      setPromoDiscount(res.data);
      toast.success("Code promo valide !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code promo invalide");
      setPromoDiscount(null);
    } finally {
      setPromoValidating(false);
    }
  };

  const handleSubscribe = async (planType) => {
    try {
      const res = await paymentsAPI.createCheckout({
        plan_type: planType,
        promo_code: promoCode || null
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`);
      // Open HTML in new window for now
      const win = window.open("", "_blank");
      win.document.write(res.data.html);
      win.document.close();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const calculateSavings = () => {
    if (!pricing) return 0;
    const monthlyAnnual = pricing.monthly.price_ttc * 12;
    return monthlyAnnual - pricing.annual.price_ttc;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <AnimatedLogo size="sm" />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Facturation</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Current Status */}
        <section>
          <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Votre abonnement</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Subscription Card */}
            <Card className={subscription ? "border-green-500" : "border-border"}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    {subscription?.plan_type === "premium" ? (
                      <Crown className="w-6 h-6 text-violet-500" />
                    ) : subscription ? (
                      <Sparkles className="w-6 h-6 text-primary" />
                    ) : (
                      <Clock className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  {subscription && (
                    <Badge variant={subscription.status === "active" ? "default" : "secondary"}>
                      {subscription.status === "active" ? "Actif" : "Essai"}
                    </Badge>
                  )}
                </div>
                
                {subscription ? (
                  <>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
                      {subscription.plan_type === "monthly" && "Abonnement Mensuel"}
                      {subscription.plan_type === "annual" && "Abonnement Annuel"}
                      {subscription.plan_type === "premium" && "Premium"}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {subscription.price_ttc}€ TTC
                      {subscription.plan_type === "monthly" ? " / mois" : " / an"}
                    </p>
                    {subscription.end_date && (
                      <p className="text-sm text-muted-foreground">
                        Expire le {formatDate(subscription.end_date)}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Aucun abonnement</h3>
                    <p className="text-muted-foreground mb-2">
                      Vous utilisez le mode à l'unité
                    </p>
                    {isTrialActive && trialEndDate && (
                      <p className="text-sm text-amber-600">
                        Période d'essai jusqu'au {formatDate(trialEndDate)}
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Credits Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <Gift className="w-6 h-6 text-green-600" />
                  </div>
                  <Badge variant="outline">{freeOperations} disponible(s)</Badge>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Opérations gratuites</h3>
                <p className="text-muted-foreground">
                  Utilisables pour activer vos tirages ou sondages sans frais
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Upgrade / Subscribe Section */}
        {!subscription && (
          <section>
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Choisir un abonnement</h2>
            
            {/* Promo Code */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Tag className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1 flex gap-2">
                    <Input
                      placeholder="Code promo"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="max-w-xs"
                    />
                    <Button 
                      variant="outline" 
                      onClick={validatePromoCode}
                      disabled={promoValidating || !promoCode}
                    >
                      {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
                    </Button>
                  </div>
                  {promoDiscount && (
                    <Badge className="bg-green-500">
                      {promoDiscount.discount_percent && `-${promoDiscount.discount_percent}%`}
                      {promoDiscount.discount_amount && `-${promoDiscount.discount_amount}€`}
                      {promoDiscount.free_operations && `+${promoDiscount.free_operations} opérations`}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Plans */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* Monthly */}
              <Card 
                className={`cursor-pointer transition-all ${selectedPlan === "monthly" ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/50"}`}
                onClick={() => setSelectedPlan("monthly")}
              >
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit' }}>Mensuel</h3>
                  <p className="text-sm text-muted-foreground mb-3">Sans engagement</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">29,90€</span>
                    <span className="text-muted-foreground"> /mois</span>
                  </div>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Tirages illimités
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Sondages illimités
                    </li>
                  </ul>
                  <Button 
                    className="w-full"
                    variant={selectedPlan === "monthly" ? "default" : "outline"}
                    onClick={(e) => { e.stopPropagation(); handleSubscribe("monthly"); }}
                  >
                    S'abonner
                  </Button>
                </CardContent>
              </Card>

              {/* Annual - BEST VALUE */}
              <Card 
                className={`cursor-pointer transition-all relative ${selectedPlan === "annual" ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/50"}`}
                onClick={() => setSelectedPlan("annual")}
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-3 py-1 rounded-full text-xs font-bold">
                  5 MOIS OFFERTS
                </div>
                <CardContent className="p-6 pt-8">
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit' }}>Annuel</h3>
                  <p className="text-sm text-primary font-medium mb-3">Meilleur rapport qualité/prix</p>
                  <div className="mb-2">
                    <span className="text-3xl font-bold">209€</span>
                    <span className="text-muted-foreground"> /an</span>
                  </div>
                  <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 mb-4">
                    <p className="text-xs text-green-700 dark:text-green-300 font-medium text-center">
                      Payez 7 mois, profitez 12 mois !
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Tirages illimités
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Sondages illimités
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Économie de 149,50€
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-primary"
                    onClick={(e) => { e.stopPropagation(); handleSubscribe("annual"); }}
                  >
                    Choisir l'annuel
                  </Button>
                </CardContent>
              </Card>

              {/* Premium */}
              <Card 
                className={`cursor-pointer transition-all bg-gradient-to-br from-violet-500/5 to-fuchsia-500/5 ${selectedPlan === "premium" ? "border-violet-500 ring-2 ring-violet-500/20" : "border-violet-300 dark:border-violet-700 hover:border-violet-500"}`}
                onClick={() => setSelectedPlan("premium")}
              >
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit' }}>Premium</h3>
                  <p className="text-sm text-violet-600 font-medium mb-3">Tout + Jeux</p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">299€</span>
                    <span className="text-muted-foreground"> /an</span>
                  </div>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-violet-500" />
                      Tout de l'Annuel
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-violet-500" />
                      Roue de la fortune
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-violet-500" />
                      Cartes à gratter
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                    onClick={(e) => { e.stopPropagation(); handleSubscribe("premium"); }}
                  >
                    Passer Premium
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* Invoices */}
        <section>
          <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Historique des factures</h2>
          
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Aucune facture pour le moment</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {invoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-4 hover:bg-muted/50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">{invoice.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium">{invoice.amount_ttc.toFixed(2)}€ TTC</p>
                          <p className="text-xs text-muted-foreground">{formatDate(invoice.created_at)}</p>
                        </div>
                        <Badge variant={invoice.status === "paid" ? "default" : "secondary"}>
                          {invoice.status === "paid" ? "Payée" : "En attente"}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => downloadInvoice(invoice.id)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        {/* TVA Info */}
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              Tous les prix affichés sont en euros (€) et incluent la TVA française de 20%. 
              Le montant HT est indiqué sur chaque facture.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
